import React, { useEffect, useRef, useState } from 'react';
import useKraTemplateStore from '../../store/kraTemplateStore';
import useKraCycleStore from '../../store/kraCycleStore';
import { getDepartments, getEmployees } from '../../services/employeeService';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';
import { matchesName } from '../../components/KRAFilters';

const emptyItemForm = {
  name: '', kpi: '', weightage: '', evidence_source: '',
  requires_external: false, external_department_ids: [],  // MULTI-DEPARTMENT: array of department IDs
};

const currentMonthValue = () => new Date().toISOString().slice(0, 7); // "YYYY-MM"

const KRALibraryPage = () => {
  const {
    templates, selectedTemplate, isSubmitting, fetchTemplates, fetchTemplate,
    createTemplate, addItem, updateItem, deactivateItem, publishTemplate,
    deleteTemplate, permanentlyDeleteTemplate, cloneTemplate, applyScoringRules,
    previewImport, commitImport,
  } = useKraTemplateStore();
  const { generateCycles, isSubmitting: isGenerating } = useKraCycleStore();
  const showToast = useUIStore((s) => s.showToast);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ department_id: '', role: '', name: '', target_employee_id: '' });
  const [showItemForm, setShowItemForm] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editForm, setEditForm] = useState(emptyItemForm);
  const [showGenPanel, setShowGenPanel] = useState(false);
  const [genMonth, setGenMonth] = useState(currentMonthValue());
  const [genDepartment, setGenDepartment] = useState('');
  const [genSkipped, setGenSkipped] = useState([]); // who Generate skipped and why - shown until the next run
  const [importFilename, setImportFilename] = useState('');
  const [importSheets, setImportSheets] = useState(null); // null = no import in review
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [cloningTemplateId, setCloningTemplateId] = useState(null); // template id currently showing its clone form
  const [cloneForm, setCloneForm] = useState({ name: '', target_employee_id: '' });
  const [showScoringRulesForm, setShowScoringRulesForm] = useState(false);
  const [bulkScoringRules, setBulkScoringRules] = useState([
    { score_value: 5, label: 'Target met or exceeded' },
    { score_value: 3, label: 'Partially met' },
    { score_value: 0, label: 'Not met' },
  ]);
  const fileInputRef = useRef(null);

  const isDraft = selectedTemplate?.status === 'draft';

  // Search over template name, the employee a personal template belongs to,
  // and its department/role - the list grows with every personal/imported
  // template, so scrolling to find one person's gets old fast.
  const [templateSearch, setTemplateSearch] = useState('');
  const visibleTemplates = templates.filter((t) =>
    matchesName(`${t.name} ${t.target_employee_name || ''} ${t.department || ''} ${t.role || ''}`, templateSearch)
  );

  useEffect(() => {
    fetchTemplates();
    getDepartments().then((res) => setDepartments(res.data.departments || []));
    getEmployees().then((res) => setEmployees(res.data.employees || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalWeightage = selectedTemplate
    ? selectedTemplate.items.filter((i) => i.is_active).reduce((sum, i) => sum + i.weightage, 0)
    : 0;
  const canPublish = selectedTemplate && Math.abs(totalWeightage - 100) < 0.01;

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    const result = await createTemplate({
      department_id: Number(newTemplate.department_id),
      role: newTemplate.role,
      name: newTemplate.name,
      target_employee_id: newTemplate.target_employee_id ? Number(newTemplate.target_employee_id) : null,
    });
    if (result.success) {
      showToast('Template created', 'success');
      setShowNewForm(false);
      setNewTemplate({ department_id: '', role: '', name: '', target_employee_id: '' });
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleDeleteTemplate = async (template) => {
    const label = template.target_employee_name ? `${template.target_employee_name}'s personal template` : `"${template.name}"`;
    if (!window.confirm(`Archive ${label}? This can't be undone, though the record stays for audit history. Existing employee KRA records generated from it are unaffected.`)) return;
    const result = await deleteTemplate(template.id);
    showToast(result.success ? 'Template archived' : result.error, result.success ? 'success' : 'error');
  };

  const handlePermanentlyDeleteTemplate = async (template) => {
    const label = template.target_employee_name ? `${template.target_employee_name}'s personal template` : `"${template.name}"`;
    if (!window.confirm(`Permanently delete ${label}? This removes it and its items completely - there is no audit trail after this, unlike Archive. Only allowed if no employee cycle was ever generated from it.`)) return;
    const result = await permanentlyDeleteTemplate(template.id);
    showToast(result.success ? result.data.message : result.error, result.success ? 'success' : 'error');
  };

  const updateBulkRule = (idx, field, value) => {
    setBulkScoringRules((rules) => rules.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addBulkRuleRow = () => {
    setBulkScoringRules((rules) => [...rules, { score_value: '', label: '' }]);
  };

  const removeBulkRuleRow = (idx) => {
    setBulkScoringRules((rules) => rules.filter((_, i) => i !== idx));
  };

  const handleApplyScoringRules = async (e) => {
    e.preventDefault();
    const rules = bulkScoringRules
      .filter((r) => r.label.trim() && r.score_value !== '')
      .map((r) => ({ score_value: Number(r.score_value), label: r.label.trim() }));
    if (rules.length === 0) {
      showToast('Add at least one rule (a score value and a label)', 'error');
      return;
    }
    const result = await applyScoringRules(selectedTemplate.id, rules);
    if (result.success) {
      showToast(result.data.message, 'success');
      setShowScoringRulesForm(false);
    } else {
      showToast(result.error, 'error');
    }
  };

  const startClone = (template) => {
    setCloningTemplateId(template.id);
    setCloneForm({ name: `${template.name} (copy)`, target_employee_id: '' });
    setShowItemForm(false);
    setEditingItemId(null);
  };

  const cancelClone = () => {
    setCloningTemplateId(null);
    setCloneForm({ name: '', target_employee_id: '' });
  };

  const handleCloneTemplate = async (e) => {
    e.preventDefault();
    const result = await cloneTemplate(cloningTemplateId, {
      name: cloneForm.name,
      target_employee_id: cloneForm.target_employee_id ? Number(cloneForm.target_employee_id) : null,
    });
    if (result.success) {
      showToast(result.data.message, 'success');
      cancelClone();
      fetchTemplate(result.data.template_id); // jump straight to the new draft so HR can tweak it
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    const result = await addItem(selectedTemplate.id, {
      name: itemForm.name,
      kpi: itemForm.kpi,
      weightage: Number(itemForm.weightage),
      evidence_source: itemForm.evidence_source,
      requires_external: itemForm.requires_external,
      external_department_ids: itemForm.requires_external ? itemForm.external_department_ids.map(Number) : [],
      scoring_rules: [{ score_value: 5, label: 'Target met or exceeded' }],
    });
    if (result.success) {
      showToast('KRA item added', 'success');
      setShowItemForm(false);
      setItemForm(emptyItemForm);
    } else {
      showToast(result.error, 'error');
    }
  };

  const startEdit = (item) => {
    setEditingItemId(item.id);
    // MULTI-DEPARTMENT: Load the external_departments list, fall back to legacy external_department_id
    const externalDeptIds = item.external_departments?.map(d => String(d.id)) || [];
    if (item.external_department_id && !externalDeptIds.includes(String(item.external_department_id))) {
      externalDeptIds.push(String(item.external_department_id));
    }
    setEditForm({
      name: item.name,
      kpi: item.kpi || '',
      weightage: item.weightage,
      evidence_source: item.evidence_source || '',
      requires_external: item.requires_external,
      external_department_ids: externalDeptIds,
    });
    setShowItemForm(false);
  };

  const cancelEdit = () => {
    setEditingItemId(null);
    setEditForm(emptyItemForm);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    const result = await updateItem(selectedTemplate.id, editingItemId, {
      name: editForm.name,
      kpi: editForm.kpi,
      weightage: Number(editForm.weightage),
      evidence_source: editForm.evidence_source,
      requires_external: editForm.requires_external,
      external_department_ids: editForm.requires_external ? editForm.external_department_ids.map(Number) : [],
    });
    if (result.success) {
      showToast('KRA item updated', 'success');
      cancelEdit();
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleRemoveItem = async (item) => {
    if (!window.confirm(`Remove "${item.name}" from this template?`)) return;
    const result = await deactivateItem(selectedTemplate.id, item.id);
    showToast(result.success ? 'KRA item removed' : result.error, result.success ? 'success' : 'error');
  };

  const handlePublish = async () => {
    const result = await publishTemplate(selectedTemplate.id);
    showToast(result.success ? 'Template published' : result.error, result.success ? 'success' : 'error');
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    const result = await generateCycles(`${genMonth}-01`, genDepartment || undefined);
    if (result.success) {
      const { created, skipped_existing, skipped_no_published_template, skipped_details } = result.data;
      setGenSkipped(skipped_details || []);
      showToast(
        `Generated ${created} cycle(s) — ${skipped_existing} already existed, ${skipped_no_published_template} skipped (no published personal or dept/role template)`,
        'success'
      );
    } else {
      showToast(result.error, 'error');
    }
  };

  // --- Excel import -------------------------------------------------------
  // Preview parses the file and returns a per-sheet breakdown - nothing is
  // saved until "Import" below is clicked. Every field here is editable
  // because the parser deliberately doesn't guess on messy source data
  // (weightages that don't total 100%, an unrecognized employee) - HR fixes
  // it here instead of it being silently wrong in the database.
  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-choosing the same file again later
    if (!file) return;
    setIsPreviewing(true);
    const result = await previewImport(file);
    setIsPreviewing(false);
    if (!result.success) {
      showToast(result.error, 'error');
      return;
    }
    setImportFilename(result.data.filename);
    setImportSheets(
      result.data.sheets.map((s, idx) => ({
        key: `${idx}-${s.sheet_name}`,
        include: s.items.length > 0,
        sheet_name: s.sheet_name,
        target_employee_id: s.matched_employee_id ? String(s.matched_employee_id) : '',
        department_id: s.matched_department_id ? String(s.matched_department_id) : '',
        role: s.matched_role || '',
        name: s.suggested_template_name || s.sheet_name,
        items: s.items.map((it) => ({ ...it, weightage: it.weightage ?? '' })),
        warnings: s.warnings,
      }))
    );
  };

  const closeImportReview = () => {
    setImportSheets(null);
    setImportFilename('');
  };

  const updateImportSheet = (key, field, value) => {
    setImportSheets((prev) => prev.map((s) => (s.key === key ? { ...s, [field]: value } : s)));
  };

  const handleImportEmployeePick = (key, employeeId) => {
    const emp = employees.find((e) => String(e.id) === String(employeeId));
    setImportSheets((prev) => prev.map((s) => (
      s.key !== key ? s : {
        ...s,
        target_employee_id: employeeId,
        department_id: emp ? String(emp.department_id) : s.department_id,
        role: emp ? emp.role : s.role,
        name: emp ? `${emp.name} - Monthly KRA` : s.name,
      }
    )));
  };

  const updateImportItem = (key, itemIdx, field, value) => {
    setImportSheets((prev) => prev.map((s) => (
      s.key !== key ? s : { ...s, items: s.items.map((it, i) => (i === itemIdx ? { ...it, [field]: value } : it)) }
    )));
  };

  const removeImportItem = (key, itemIdx) => {
    setImportSheets((prev) => prev.map((s) => (
      s.key !== key ? s : { ...s, items: s.items.filter((_, i) => i !== itemIdx) }
    )));
  };

  const importWeightageSum = (sheet) =>
    Math.round(sheet.items.reduce((sum, it) => sum + (Number(it.weightage) || 0), 0) * 100) / 100;

  const handleConfirmImport = async () => {
    const toImport = importSheets.filter((s) => s.include && s.items.length > 0);
    if (toImport.length === 0) {
      showToast('Select at least one sheet with items to import', 'error');
      return;
    }
    const missingDept = toImport.find((s) => !s.department_id);
    if (missingDept) {
      showToast(`"${missingDept.sheet_name}" has no department set — pick one before importing`, 'error');
      return;
    }

    const payload = {
      templates: toImport.map((s) => ({
        target_employee_id: s.target_employee_id ? Number(s.target_employee_id) : null,
        department_id: Number(s.department_id),
        role: s.role,
        name: s.name,
        items: s.items.map((it) => ({
          name: it.name,
          kpi: it.kpi || null,
          description: it.description || null,
          weightage: Number(it.weightage) || 0,
          evidence_source: it.evidence_source || null,
        })),
      })),
    };

    const result = await commitImport(payload);
    if (result.success) {
      showToast(result.data.message, 'success');
      closeImportReview();
    } else {
      showToast(result.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white">KRA Library</h1>
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xlsm"
              onChange={handleFileChosen}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isPreviewing}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-4 py-2 rounded disabled:opacity-60"
            >
              {isPreviewing ? 'Reading file...' : 'Import from Excel'}
            </button>
            <button onClick={() => setShowGenPanel((v) => !v)} className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-4 py-2 rounded">
              Generate Monthly Cycles
            </button>
            <button onClick={() => setShowNewForm((v) => !v)} className="bg-komal-blue hover:bg-komal-blue-dark text-white text-sm font-semibold px-4 py-2 rounded">
              + New Template
            </button>
          </div>
        </div>

        {showGenPanel && (
          <form onSubmit={handleGenerate} className="glass-card p-5 mb-6">
            <p className="text-xs text-gray-500 mb-3">
              Creates the KRA record for the selected month, for every active employee whose department + role has a{' '}
              <strong>published</strong> template. Nothing appears in My KRA, Team KRA, or Cross Inputs for anyone
              until this has been run for that month — safe to re-run, existing records are left untouched.
              {' '}<strong>Not limited to the current month</strong> — pick any past month to backfill old appraisal
              data (e.g. generate January before self/HOD scores are entered for it), or generate several months
              in a row to catch up.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
                <input required type="month" value={genMonth} onChange={(e) => setGenMonth(e.target.value)} max={currentMonthValue()} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                <p className="text-xs text-gray-400 mt-1">Any past month is fine — this is how you backfill old data.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Department (optional)</label>
                <select value={genDepartment} onChange={(e) => setGenDepartment(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                  <option value="">All Departments</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <button type="submit" disabled={isGenerating} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
            </div>
            {genSkipped.length > 0 && (
              <div className="mt-4 border border-amber-200 bg-amber-50 rounded p-3 text-xs text-amber-900">
                <div className="font-semibold mb-1">Skipped - no cycle created ({genSkipped.length}):</div>
                <ul className="space-y-1 max-h-48 overflow-y-auto">
                  {genSkipped.map((s) => (
                    <li key={s.employee_id}><strong>{s.name}</strong> - {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </form>
        )}

        {showNewForm && (
          <form onSubmit={handleCreateTemplate} className="glass-card p-5 mb-6 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
                <select required value={newTemplate.department_id} onChange={(e) => setNewTemplate((f) => ({ ...f, department_id: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                  <option value="">Select</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                <input required value={newTemplate.role} onChange={(e) => setNewTemplate((f) => ({ ...f, role: e.target.value }))} placeholder="e.g. Engineer" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Template name</label>
                <input required value={newTemplate.name} onChange={(e) => setNewTemplate((f) => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Create</button>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Personal override for one employee (optional)</label>
              <select value={newTemplate.target_employee_id} onChange={(e) => setNewTemplate((f) => ({ ...f, target_employee_id: e.target.value }))} className="w-full md:w-1/2 border border-gray-300 rounded px-3 py-2 text-sm">
                <option value="">No — shared by everyone in this department + role</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Pick a name here to make this template exclusive to that one employee — it takes priority over the
                shared department/role template whenever their monthly cycle is generated, without affecting anyone else.
              </p>
            </div>
          </form>
        )}

        {importSheets ? (
          <div className="glass-card p-5 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold text-gray-900">Review import — {importFilename}</h2>
                <p className="text-xs text-gray-500 mt-1 max-w-2xl">
                  Nothing is saved yet. Check the matched employee, fix any weightage warnings below, then confirm —
                  each sheet becomes a new draft template with the same edit / scoring-rule / publish steps as one
                  created by hand.
                </p>
              </div>
              <button onClick={closeImportReview} className="text-sm text-gray-500 hover:text-gray-700 font-semibold whitespace-nowrap">
                Cancel
              </button>
            </div>

            {importSheets.map((sheet) => {
              const sum = importWeightageSum(sheet);
              const sumOk = sheet.items.length > 0 && Math.abs(sum - 100) < 0.01;
              return (
                <div key={sheet.key} className={`border rounded-lg ${sheet.include ? 'border-gray-200' : 'border-gray-100 opacity-50'}`}>
                  <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100 rounded-t-lg">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mr-1">
                      <input
                        type="checkbox"
                        checked={sheet.include}
                        onChange={(e) => updateImportSheet(sheet.key, 'include', e.target.checked)}
                      />
                      {sheet.sheet_name}
                    </label>
                    <select
                      value={sheet.target_employee_id}
                      onChange={(e) => handleImportEmployeePick(sheet.key, e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                      title="Which employee this template belongs to"
                    >
                      <option value="">No specific employee (dept/role template)</option>
                      {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                    <select
                      value={sheet.department_id}
                      onChange={(e) => updateImportSheet(sheet.key, 'department_id', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option value="">Department...</option>
                      {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <input
                      value={sheet.role}
                      onChange={(e) => updateImportSheet(sheet.key, 'role', e.target.value)}
                      placeholder="Role"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-28"
                    />
                    <input
                      value={sheet.name}
                      onChange={(e) => updateImportSheet(sheet.key, 'name', e.target.value)}
                      placeholder="Template name"
                      className="border border-gray-300 rounded px-2 py-1 text-xs flex-1 min-w-[180px]"
                    />
                    <span className={`text-xs font-bold ml-auto ${sumOk ? 'text-green-700' : 'text-red-700'}`}>{sum}%</span>
                  </div>

                  {sheet.warnings.length > 0 && (
                    <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 space-y-0.5">
                      {sheet.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                    </div>
                  )}

                  {sheet.include && sheet.items.length > 0 && (
                    <ScrollX>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-500 border-b border-gray-100">
                            <th className="px-3 py-1.5 font-medium">Name</th>
                            <th className="px-3 py-1.5 font-medium">KPI</th>
                            <th className="px-3 py-1.5 font-medium w-24">Weight %</th>
                            <th className="px-3 py-1.5 font-medium w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sheet.items.map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-50 last:border-0">
                              <td className="px-3 py-1.5">
                                <input
                                  value={item.name}
                                  onChange={(e) => updateImportItem(sheet.key, idx, 'name', e.target.value)}
                                  className="w-full border border-gray-200 rounded px-2 py-1"
                                />
                              </td>
                              <td className="px-3 py-1.5">
                                <input
                                  value={item.kpi || ''}
                                  onChange={(e) => updateImportItem(sheet.key, idx, 'kpi', e.target.value)}
                                  className="w-full border border-gray-200 rounded px-2 py-1"
                                />
                              </td>
                              <td className="px-3 py-1.5">
                                <input
                                  type="number"
                                  value={item.weightage}
                                  onChange={(e) => updateImportItem(sheet.key, idx, 'weightage', e.target.value)}
                                  className="w-full border border-gray-200 rounded px-2 py-1"
                                />
                              </td>
                              <td className="px-3 py-1.5">
                                <button type="button" onClick={() => removeImportItem(sheet.key, idx)} className="text-red-500 hover:text-red-700 font-semibold">
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollX>
                  )}
                </div>
              );
            })}

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
              <button onClick={closeImportReview} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 font-semibold">
                Cancel
              </button>
              <button
                onClick={handleConfirmImport}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {isSubmitting ? 'Importing...' : `Import ${importSheets.filter((s) => s.include).length} template(s) as drafts`}
              </button>
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="glass-card divide-y divide-gray-100">
            <div className="px-4 py-3 font-semibold text-gray-900 text-sm border-b border-gray-100">Templates</div>
            <div className="px-4 py-2">
              <input
                type="search"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder="Search by name..."
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
              />
            </div>
            {templates.length > 0 && visibleTemplates.length === 0 && (
              <p className="px-4 py-3 text-xs text-gray-500">No templates match "{templateSearch.trim()}".</p>
            )}
            {visibleTemplates.map((t) => (
              <button
                key={t.id}
                onClick={() => fetchTemplate(t.id)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm ${selectedTemplate?.id === t.id ? 'bg-gray-50' : ''}`}
              >
                <div className="font-medium text-gray-900">{t.name}</div>
                <div className="text-xs text-gray-500">
                  {t.target_employee_name ? `${t.target_employee_name} (personal)` : `${t.department} · ${t.role}`} · v{t.version}
                </div>
                <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded ${
                  t.status === 'published' ? 'bg-green-100 text-green-800'
                  : t.status === 'archived' ? 'bg-gray-200 text-gray-600'
                  : 'bg-amber-100 text-amber-800'
                }`}>
                  {t.status}
                </span>
              </button>
            ))}
          </div>

          <div className="md:col-span-2 glass-card">
            {!selectedTemplate ? (
              <p className="p-6 text-sm text-gray-500">Select a template to edit its KRA items.</p>
            ) : (
              <>
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h2 className="font-semibold text-gray-900">{selectedTemplate.name}</h2>
                    <p className="text-xs text-gray-500">
                      {selectedTemplate.target_employee_name ? `${selectedTemplate.target_employee_name} (personal)` : `${selectedTemplate.department} · ${selectedTemplate.role}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {isDraft && (
                      <button onClick={() => { setShowItemForm((v) => !v); cancelEdit(); }} className="text-sm text-komal-blue font-semibold">+ Add KRA item</button>
                    )}
                    {isDraft && (
                      <button onClick={() => setShowScoringRulesForm((v) => !v)} className="text-sm text-komal-blue font-semibold">Set Scoring Rules for All Items</button>
                    )}
                    {selectedTemplate.status !== 'archived' && (
                      <button onClick={() => startClone(selectedTemplate)} className="text-sm text-gray-600 hover:text-gray-900 font-semibold">Clone…</button>
                    )}
                    {selectedTemplate.status !== 'archived' && (
                      <button onClick={() => handleDeleteTemplate(selectedTemplate)} className="text-sm text-red-600 hover:text-red-700 font-semibold">Delete</button>
                    )}
                    {selectedTemplate.status === 'archived' && (
                      <button onClick={() => handlePermanentlyDeleteTemplate(selectedTemplate)} className="text-sm text-red-600 hover:text-red-700 font-semibold" title="Only allowed if no employee cycle was ever generated from this template">
                        Delete Permanently
                      </button>
                    )}
                  </div>
                </div>

                {cloningTemplateId === selectedTemplate.id && (
                  <form onSubmit={handleCloneTemplate} className="p-5 border-b border-gray-100 bg-gray-50 space-y-2">
                    <p className="text-xs text-gray-500">
                      Copies every active item, its scoring rules, and its external-department requirements into a new
                      draft — the source template ({selectedTemplate.status}) is left untouched. Tweak the copy, then publish it when ready.
                    </p>
                    <input required placeholder="New template name" value={cloneForm.name} onChange={(e) => setCloneForm((f) => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Make it personal to one employee (optional)</label>
                      <select value={cloneForm.target_employee_id} onChange={(e) => setCloneForm((f) => ({ ...f, target_employee_id: e.target.value }))} className="w-full md:w-1/2 border border-gray-300 rounded px-3 py-2 text-sm">
                        <option value="">
                          {selectedTemplate.target_employee_name ? `Keep it personal to ${selectedTemplate.target_employee_name}` : 'No — keep it shared by department + role'}
                        </option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                        {isSubmitting ? 'Cloning...' : 'Clone as new draft'}
                      </button>
                      <button type="button" onClick={cancelClone} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 font-semibold">Cancel</button>
                    </div>
                  </form>
                )}

                {showScoringRulesForm && isDraft && (
                  <form onSubmit={handleApplyScoringRules} className="p-5 border-b border-gray-100 bg-gray-50 space-y-2">
                    <p className="text-xs text-gray-500">
                      Overwrites the scoring rules on every active item in this template with the same set — the fix
                      for items showing "No scoring rule" (most commonly: items imported from Excel, which don't get
                      one automatically). Applies to all items at once; edit an individual item afterward if one needs different wording.
                    </p>
                    {bulkScoringRules.map((rule, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <input
                          type="number" min="0" max="5" placeholder="Score" value={rule.score_value}
                          onChange={(e) => updateBulkRule(idx, 'score_value', e.target.value)}
                          className="w-20 border border-gray-300 rounded px-3 py-2 text-sm"
                        />
                        <input
                          placeholder="What this score means" value={rule.label}
                          onChange={(e) => updateBulkRule(idx, 'label', e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                        />
                        <button type="button" onClick={() => removeBulkRuleRow(idx)} className="text-red-500 hover:text-red-700 font-semibold px-2">✕</button>
                      </div>
                    ))}
                    <button type="button" onClick={addBulkRuleRow} className="text-sm text-komal-blue font-semibold">+ Add another rule</button>
                    <div className="flex gap-2 pt-1">
                      <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                        {isSubmitting ? 'Applying...' : 'Apply to All Items'}
                      </button>
                      <button type="button" onClick={() => setShowScoringRulesForm(false)} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 font-semibold">Cancel</button>
                    </div>
                  </form>
                )}

                {!isDraft && (
                  <div className="px-5 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
                    This template is {selectedTemplate.status} — only draft templates can be edited.
                    {selectedTemplate.status === 'published' && ' Clone it to make changes for next month without disturbing the version currently in use.'}
                  </div>
                )}

                {showItemForm && (
                  <form onSubmit={handleAddItem} className="p-5 border-b border-gray-100 space-y-2 bg-gray-50">
                    <input required placeholder="KRA name" value={itemForm.name} onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    <input placeholder="KPI / target description" value={itemForm.kpi} onChange={(e) => setItemForm((f) => ({ ...f, kpi: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    <div className="grid grid-cols-2 gap-2">
                      <input required type="number" min="0" max="100" placeholder="Weightage %" value={itemForm.weightage} onChange={(e) => setItemForm((f) => ({ ...f, weightage: e.target.value }))} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                      <input placeholder="Evidence source" value={itemForm.evidence_source} onChange={(e) => setItemForm((f) => ({ ...f, evidence_source: e.target.value }))} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={itemForm.requires_external} onChange={(e) => setItemForm((f) => ({ ...f, requires_external: e.target.checked }))} />
                      Requires external department score(s)
                    </label>
                    {itemForm.requires_external && (
                      <div className="pl-6 space-y-1 border-l-2 border-gray-200">
                        <p className="text-xs font-medium text-gray-600 mb-2">Select one or more departments:</p>
                        {departments.map((d) => (
                          <label key={d.id} className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={itemForm.external_department_ids.includes(String(d.id))}
                              onChange={(e) => {
                                const updated = e.target.checked
                                  ? [...itemForm.external_department_ids, String(d.id)]
                                  : itemForm.external_department_ids.filter(id => id !== String(d.id));
                                setItemForm((f) => ({ ...f, external_department_ids: updated }));
                              }}
                            />
                            {d.name}
                          </label>
                        ))}
                      </div>
                    )}
                    <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                      Add item
                    </button>
                  </form>
                )}

                <ScrollX>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="px-5 py-2 font-medium">KRA</th>
                        <th className="px-5 py-2 font-medium">Weight</th>
                        <th className="px-5 py-2 font-medium">External</th>
                        {isDraft && <th className="px-5 py-2 font-medium">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTemplate.items.filter((i) => i.is_active).map((item) => (
                        editingItemId === item.id ? (
                          <tr key={item.id} className="border-b border-gray-50 last:border-0 bg-gray-50">
                            <td colSpan={isDraft ? 4 : 3} className="px-5 py-3">
                              <form onSubmit={handleUpdateItem} className="space-y-2">
                                <input required placeholder="KRA name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                                <input placeholder="KPI / target description" value={editForm.kpi} onChange={(e) => setEditForm((f) => ({ ...f, kpi: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                                <div className="grid grid-cols-2 gap-2">
                                  <input required type="number" min="0" max="100" placeholder="Weightage %" value={editForm.weightage} onChange={(e) => setEditForm((f) => ({ ...f, weightage: e.target.value }))} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                                  <input placeholder="Evidence source" value={editForm.evidence_source} onChange={(e) => setEditForm((f) => ({ ...f, evidence_source: e.target.value }))} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                                </div>
                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={editForm.requires_external} onChange={(e) => setEditForm((f) => ({ ...f, requires_external: e.target.checked }))} />
                                  Requires external department score(s)
                                </label>
                                {editForm.requires_external && (
                                  <div className="pl-6 space-y-1 border-l-2 border-gray-200">
                                    <p className="text-xs font-medium text-gray-600 mb-2">Select one or more departments:</p>
                                    {departments.map((d) => (
                                      <label key={d.id} className="flex items-center gap-2 text-sm text-gray-700">
                                        <input
                                          type="checkbox"
                                          checked={editForm.external_department_ids.includes(String(d.id))}
                                          onChange={(e) => {
                                            const updated = e.target.checked
                                              ? [...editForm.external_department_ids, String(d.id)]
                                              : editForm.external_department_ids.filter(id => id !== String(d.id));
                                            setEditForm((f) => ({ ...f, external_department_ids: updated }));
                                          }}
                                        />
                                        {d.name}
                                      </label>
                                    ))}
                                  </div>
                                )}
                                <div className="flex gap-2">
                                  <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                                    Save changes
                                  </button>
                                  <button type="button" onClick={cancelEdit} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 font-semibold">
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </td>
                          </tr>
                        ) : (
                          <tr key={item.id} className="border-b border-gray-50 last:border-0">
                            <td className="px-5 py-3">
                              <div className="font-medium text-gray-900">{item.name}</div>
                              <div className="text-xs text-gray-500">{item.kpi}</div>
                            </td>
                            <td className="px-5 py-3 font-semibold">{item.weightage}%</td>
                            <td className="px-5 py-3 text-xs text-gray-500">
                              {item.requires_external ? (
                                <>
                                  <div className="space-y-0.5">
                                    {item.external_departments?.map(d => (
                                      <div key={d.id} className="inline-block bg-blue-50 text-blue-700 px-2 py-0.5 rounded mr-1 mb-0.5">
                                        {d.name}
                                      </div>
                                    ))}
                                  </div>
                                  {!item.scoring_rules?.length && <div className="text-amber-600 mt-1">No scoring rule</div>}
                                </>
                              ) : '—'}
                            </td>
                            {isDraft && (
                              <td className="px-5 py-3">
                                <div className="flex gap-3">
                                  <button onClick={() => startEdit(item)} className="text-xs text-komal-blue font-semibold">Edit</button>
                                  <button onClick={() => handleRemoveItem(item)} className="text-xs text-red-600 font-semibold">Remove</button>
                                </div>
                              </td>
                            )}
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </ScrollX>

                <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div>
                    <span className={`text-xl font-bold ${canPublish ? 'text-green-700' : 'text-red-700'}`}>{totalWeightage}%</span>
                    <span className="text-xs text-gray-500 ml-2">
                      {canPublish ? 'Total is 100% — ready to publish' : 'Active item weightage must total exactly 100% before publishing'}
                    </span>
                  </div>
                  <button
                    onClick={handlePublish}
                    disabled={!canPublish || isSubmitting || selectedTemplate.status === 'published'}
                    title={!canPublish ? 'Fix weightage to 100% first' : ''}
                    className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {selectedTemplate.status === 'published' ? 'Published' : 'Publish Template'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default KRALibraryPage;
